// SSR-safe localStorage helpers. Before this module the same defensive idiom
// (`typeof localStorage === 'undefined'` guard + try/catch around get/set) was
// hand-rolled in ~13 stores, each re-deriving its own load/persist pair and its
// own encoding. That scatter is the source of two bugs: some stores forgot the
// `undefined` guard (a raw `localStorage.getItem` THROWS in an SSR / non-browser
// context, or when WebKit disables storage for a plain-http insecure origin),
// and the try/catch boilerplate drifted between copies.
//
// These helpers centralize the guard + try/catch. Reads return `fallback` when
// storage is unavailable, the key is absent, or anything throws; writes are
// best-effort and silently no-op on quota / private-mode / SSR. Callers pick the
// helper whose ENCODING matches what they already persist so no stored value's
// on-disk format changes:
//   - readBool / writeBool: a boolean stored as the string '1' / '0'.
//   - readNumber / writeNumber: a finite number stored via String(n).
//   - readString / writeString: a raw string value.
//   - readJson / writeJson: any JSON-serializable value via JSON.stringify.
// Validated / coerced shapes (a sanitized id array, an enum, a shape-checked
// object) read through readJson and apply their own check to the returned value.

// A single guarded accessor for the underlying Storage. Returns undefined
// whenever localStorage is not usable (SSR, or the browser throwing merely on
// property access in a locked-down context), so every helper below can bail to
// its fallback uniformly.
function getStorage(): Storage | undefined {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    return localStorage;
  } catch {
    return undefined;
  }
}

/** Read a raw string. Returns `fallback` when absent or storage is unavailable. */
export function readString(key: string, fallback: string): string {
  const storage = getStorage();
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(key);
    return raw === null ? fallback : raw;
  } catch {
    return fallback;
  }
}

/** Write a raw string. Best-effort: silently no-ops on quota / private-mode / SSR. */
export function writeString(key: string, value: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Storage can throw in private mode / quota-exceeded; the caller's in-memory
    // state still holds, the only loss is persistence across reloads.
  }
}

/**
 * Read a boolean stored as the string '1' (true) / '0' (false). Matches the
 * legacy `getItem(key) === '1'` idiom: any value other than '1' (including the
 * key being absent) yields `fallback`, and an absent key yields `fallback`.
 */
export function readBool(key: string, fallback = false): boolean {
  const storage = getStorage();
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(key);
    if (raw === null) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

/** Write a boolean as the string '1' (true) / '0' (false). */
export function writeBool(key: string, value: boolean): void {
  writeString(key, value ? '1' : '0');
}

/**
 * Read a finite number stored via `String(n)`. Returns `fallback` when the key
 * is absent, storage is unavailable, or the stored text is not a finite number.
 */
export function readNumber(key: string, fallback: number): number {
  const storage = getStorage();
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(key);
    if (raw === null) return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

/** Write a number via `String(n)`. */
export function writeNumber(key: string, value: number): void {
  writeString(key, String(value));
}

/**
 * Read a JSON-serialized value. Returns `fallback` when the key is absent,
 * storage is unavailable, or the stored text fails to parse. The parsed value is
 * returned as-is (typed `T` by the caller) so callers that need to validate or
 * coerce the shape can do so on the result.
 */
export function readJson<T>(key: string, fallback: T): T {
  const storage = getStorage();
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Write a value as JSON via `JSON.stringify`. */
export function writeJson(key: string, value: unknown): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can throw in private mode / quota-exceeded; the caller's in-memory
    // state still holds, the only loss is persistence across reloads.
  }
}

/** Remove a key. Best-effort: silently no-ops when storage is unavailable. */
export function removeKey(key: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}
